import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import {
  getOffersAreas,
  getOffersDevelopers,
  getOffersFilterOptions
} from '../../../new-lib/apis';
import { useDispatch, useSelector } from 'react-redux';
import SelectionFilter from './filters/SelectionFilter';
import NawyExclusiveFilter from './filters/NawyExclusiveFilter';
import useOnClickOutside from '../../../hooks/ClickOutside';
import { resetFilters } from '../../../redux/slices/offersSlice';
import { useTranslation } from 'next-i18next';

const CityscapeOffersFilter = () => {
  const { t } = useTranslation('cityscape');
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const developersIds = useSelector(
    (state: any) => state.offers.developers_ids
  );
  const areasIds = useSelector((state: any) => state.offers.areas_ids);
  const filterTypes = useSelector((state: any) => state.offers.offers_filters);

  const filtersRef = useRef(null);

  const dispatch = useDispatch();

  const [filterDropdown, setFilterDropdown] = useState<
    'offer_type' | 'Developers_Search' | 'Areas_search' | null
  >(null);

  const { locale } = useRouter();

  const handleClickOutside = () => {
    if (filterDropdown !== null && !isMobile) {
      setFilterDropdown(null);
    }
  };

  useOnClickOutside(filtersRef, filtersRef, handleClickOutside);

  const resetOffersFilters = () => {
    dispatch(resetFilters());
  };

  const {
    data: offersFilters,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['offers-filters', locale],
    queryFn: () => getOffersFilterOptions(locale),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const { data: developers } = useQuery({
    queryKey: ['developers', locale],
    queryFn: () => getOffersDevelopers(locale),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const { data: areas } = useQuery({
    queryKey: ['areas', locale],
    queryFn: () => getOffersAreas(locale),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const renderOffersFilters = useCallback(() => {
    if (offersFilters) {
      const hasNawyExclusive = offersFilters.offers_search.some(
        (obj: any) => obj.key === 'nawy_exclusive'
      );

      return (
        <>
          {hasNawyExclusive && <NawyExclusiveFilter />}

          {offersFilters.offers_search
            .filter((obj: any) => obj.key !== 'nawy_exclusive')
            .map((obj: any) => (
              <SelectionFilter
                key={obj.key}
                title={obj.value}
                type={obj.key}
                selections={
                  obj.key === 'Developers_Search' ? developers : areas
                }
                filterDropdown={filterDropdown}
                setFilterDropdown={setFilterDropdown}
              />
            ))}

          {offersFilters.offer_types.length > 0 ? (
            <SelectionFilter
              title="offer_types"
              type="offer_type"
              selections={offersFilters.offer_types}
              filterDropdown={filterDropdown}
              setFilterDropdown={setFilterDropdown}
            />
          ) : (
            <> </>
          )}
        </>
      );
    }
  }, [offersFilters, isLoading, isError, filterDropdown]);

  return (
    <OffersContainer id="filters" ref={filtersRef} isMobile={isMobile}>
      {renderOffersFilters()}

      {(developersIds.length > 0 ||
        areasIds.length > 0 ||
        filterTypes.length > 0) && (
        <span className="reset-all" onClick={resetOffersFilters}>
          {t('reset_all')}
        </span>
      )}
    </OffersContainer>
  );
};

const OffersContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 8px 8px;
  background-color: #fff;

  .reset-all {
    font-family: materialRegular;
    font-size: 12px;
    color: #1e6dac;
    cursor: pointer;
    text-decoration: underline;
    white-space: nowrap;
  }

  ${({ isMobile }) =>
    isMobile
      ? 'overflow-x: scroll; top: 60px; '
      : 'overflow-x: unset; overflow-y: visible; width: fit-content;'}

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  .offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    min-width: 95px;
    padding: 12px 8px;
    border-radius: 8px;
    border: 1px solid #e4ebf2;
    cursor: pointer;

    .title {
      font-family: materialBold;
      font-size: ${props => props.theme.font.sizes.xl};
      color: ${props => props.theme.light.colors.secondaryBlue};
    }

    .subtitle {
      font-family: materialRegular;
      font-size: 10px;
      color: #313131;
    }

    &.selected {
      background: ${props => props.theme.light.colors.primaryBlue};
      .title,
      .subtitle {
        color: white;
      }
    }

    @media screen and (max-width: ${props =>
        props.theme.breakPoints.mobile.max}) {
      z-index: 9999;
      position: sticky;
      top: 70px;
    }

    @media screen and (min-width: ${props =>
        props.theme.breakPoints.desktop.min}) {
      :hover {
        background: ${props => props.theme.light.colors.whitish};

        .title {
          color: ${props => props.theme.light.colors.secondaryBlue};
        }

        .subtitle {
          color: #313131;
        }
      }
    }
  }
`;
export default CityscapeOffersFilter;
